import {DATABASE_NAME, DATABASE_VERSION} from './DevServiceWorkerConstants';
import BrowserState, {DEFAULT_BROWSER_STATE} from './stores/BrowserState';
import CachedEndpoint, {CachedEndpointWithId} from './stores/CachedEndpoint';

export enum IDBStoreType {
  BROWSER_STATE = 'browser_state',
  CACHED_ENDPOINTS = 'cached_endpoints',
}

export const BROWSER_STATE_KEY = 'dev-service-worker';
export type IDBStoreKey = typeof BROWSER_STATE_KEY;

export function openBrowserStateStore(db: IDBDatabase): IDBObjectStore {
  return db.transaction([IDBStoreType.BROWSER_STATE], 'readwrite').objectStore(IDBStoreType.BROWSER_STATE);
}

export function openEndpointsDataStore(db: IDBDatabase): IDBObjectStore {
  return db
    .transaction([IDBStoreType.CACHED_ENDPOINTS], 'readwrite')
    .objectStore(IDBStoreType.CACHED_ENDPOINTS);
}

export function migrateExistingData(db: IDBDatabase, ev: IDBVersionChangeEvent): void {
  const endpointsDataStore = openEndpointsDataStore(db);
  if (ev.oldVersion < 2) {
    // Backfill bypassCache + cacheHitClientIds.
    dbGetAll<CachedEndpoint>(endpointsDataStore).then((endpoints) => {
      endpoints.forEach((endpoint: CachedEndpointWithId) => {
        const endpointKey = endpoint.id;
        const migratedEndpoint: CachedEndpoint & {id?: IDBValidKey} = {
          ...endpoint,
          bypassCache: endpoint.bypassCache || false,
          cacheHitClientIds: endpoint.cacheHitClientIds || [],
        };
        delete migratedEndpoint.id;
        dbPut(endpointsDataStore, endpointKey, migratedEndpoint);
      });
    });
  }
}

// This runs if and only if the dev-service-worker is in the `install` phase and
// `DevServiceWorkerConstants.DATABASE_VERSION` is greater than the current IndexedDB version.
// See: https://developer.mozilla.org/en-US/docs/Web/API/IDBOpenDBRequest/onupgradeneeded
export function upgradeIndexedDB(this: IDBOpenDBRequest, ev: IDBVersionChangeEvent): void {
  const db = this.result;
  if (!db.objectStoreNames.contains(IDBStoreType.CACHED_ENDPOINTS)) {
    db.createObjectStore(IDBStoreType.CACHED_ENDPOINTS);
  }
  if (!db.objectStoreNames.contains(IDBStoreType.BROWSER_STATE)) {
    const browserStateStore = db.createObjectStore(IDBStoreType.BROWSER_STATE);
    dbPut(browserStateStore, BROWSER_STATE_KEY, DEFAULT_BROWSER_STATE);
  }
  // (We're guaranteed to have a transaction because the request is open until we return.)
  this.transaction!.oncomplete = () => {
    migrateExistingData(db, ev);
  };
}

function wait<R>(request: IDBRequest<R>): Promise<R> {
  return new Promise((resolve, reject) => {
    function onSuccess(this: IDBRequest<R>): void {
      resolve(this.result);
    }
    function onError(this: IDBRequest<R>): void {
      reject(this.error);
    }
    request.onsuccess = onSuccess;
    request.onerror = onError;
  });
}

export function dbConnect(context: {indexedDB: IDBFactory}): Promise<IDBDatabase> {
  return wait(context.indexedDB.open(DATABASE_NAME, DATABASE_VERSION));
}

export function dbGet<K extends IDBValidKey, V extends IndexedDBStore>(
  store: IDBObjectStore,
  key: K
): Promise<V | undefined> {
  return wait(store.get(key));
}

function dbGetAllKeys(store: IDBObjectStore): Promise<Array<IDBValidKey>> {
  return wait(store.getAllKeys());
}

export async function dbGetAll<V extends IndexedDBStore>(
  store: IDBObjectStore
): Promise<Array<V & {id: IDBValidKey}>> {
  const keys = await dbGetAllKeys(store);
  return await Promise.all(
    keys.map(async (key) => {
      const endpointData = (await dbGet(store, key)) as V;
      return {
        id: key,
        ...endpointData,
      };
    })
  );
}

export function dbPut<K extends IDBValidKey, V extends IndexedDBStore>(
  store: IDBObjectStore,
  key: K,
  value: V
): Promise<K | undefined> {
  // Cast response because we know the types will always line up (because we always specify a key)
  return wait(store.put(value, key)) as Promise<K | undefined>;
}

export function dbDelete<K extends IDBValidKey>(store: IDBObjectStore, key: K): Promise<undefined> {
  return wait(store.delete(key));
}

export function dbDeleteAll(store: IDBObjectStore): Promise<undefined> {
  return wait(store.clear());
}

type IndexedDBStore = BrowserState | CachedEndpoint;
